import React, { useState, useEffect, useCallback } from "react";
import { Badge } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { LinkPrimary } from "jsx/components/CitoPlus/components/CitoPlusLink";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import ReservaModal from "./ReservaModal";

import "../../../table/FilteringTable/filtering.css";

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ZonaSocialCard = ({ zona, onClick }) => {
  const formatDiasOperacion = (dias) => {
    if (!dias || dias.length === 0) return "No disponible";
    if (dias.length === 7) return "Todos los días";
    if (JSON.stringify([...dias].sort()) === JSON.stringify([0, 1, 2, 3, 4]))
      return "Lun - Vie";
    return dias.map((d) => DIAS_SEMANA[d]).join(", ");
  };

  return (
    <div
      className="card position-relative overflow-hidden zona-social-card"
      onClick={() => onClick(zona)}
      style={{ height: "200px", cursor: "pointer", border: "none" }}
    >
      {zona.imagen_url ? (
        <img
          src={zona.imagen_url}
          alt={zona.nombre}
          className="position-absolute w-100 h-100 zona-social-card-img"
          style={{ objectFit: "cover", transition: "transform 0.3s ease" }}
        />
      ) : (
        <div
          className="position-absolute w-100 h-100"
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #495057 50%, #343a40 100%)",
          }}
        />
      )}

      <div
        className="position-absolute w-100 h-100"
        style={{
          background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)",
        }}
      />

      <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-between p-3 text-white">
        <div className="d-flex justify-content-between align-items-start">
          <h5 className="mb-0 fw-bold" style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.5)" }}>
            {zona.nombre}
          </h5>
          <Badge
            bg={zona.tipo_reserva === "hora" ? "info" : "primary"}
            className="ms-2"
          >
            {zona.tipo_reserva === "hora"
              ? `Por hora (${zona.tiempo_maximo_reserva}h)`
              : "Por día"}
          </Badge>
        </div>

        <div className="row g-2">
          <div className="col-6">
            <small className="d-flex align-items-center">
              <i className="fas fa-clock me-2"></i>
              {zona.horario_inicio} - {zona.horario_fin}
            </small>
          </div>
          <div className="col-6">
            <small className="d-flex align-items-center">
              <i className="fas fa-calendar me-2"></i>
              {formatDiasOperacion(zona.dias_operacion)}
            </small>
          </div>
          <div className="col-6">
            <small className="d-flex align-items-center">
              <i className="fas fa-users me-2"></i>
              {zona.capacidad_min} - {zona.capacidad_max} personas
            </small>
          </div>
          {zona.requiere_aprobacion_manual ? (
            <div className="col-6">
              <Badge bg="warning" text="dark">
                <i className="fas fa-exclamation-triangle me-1"></i>
                Requiere aprobación
              </Badge>
            </div>
          ) : zona.requiere_pago && zona.valor > 0 ? (
            <div className="col-6">
              <Badge bg="success">
                <i className="fas fa-dollar-sign me-1"></i>
                ${zona.valor.toLocaleString("es-CO")}
              </Badge>
            </div>
          ) : (
            <div className="col-6">
              <Badge bg="secondary">
                <i className="fas fa-check-circle me-1"></i>
                Gratuito
              </Badge>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-end">
          {zona.reglas_uso && (
            <small className="text-truncate" style={{ maxWidth: "60%", textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}>
              <i className="fas fa-info-circle me-1"></i>
              {zona.reglas_uso.replace(/<[^>]*>/g, "").substring(0, 50)}
              {zona.reglas_uso.length > 50 ? "..." : ""}
            </small>
          )}
          <Badge bg="light" text="dark" className="ms-auto">
            Reservar <i className="fas fa-arrow-right ms-1"></i>
          </Badge>
        </div>
      </div>
    </div>
  );
};

const ZonasDisponibles = () => {
  const [loading, setLoading] = useState(true);
  const [zonas, setZonas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);

  const fetchZonas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ResidenteService.getZonas();
      setZonas(response.data.results || response.data);
    } catch (error) {
      handleErrors(error);
      notifyError("Error al cargar las zonas sociales");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZonas();
  }, [fetchZonas]);

  const handleCardClick = (zona) => {
    setSelectedZona(zona);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedZona(null);
  };

  const handleReservaSuccess = () => {
    fetchZonas();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <SpinnerCitoPlus text="Cargando zonas sociales..." />
      </div>
    );
  }

  if (zonas.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <i className="fas fa-building fa-3x text-muted mb-3"></i>
          <h5>No hay zonas sociales disponibles</h5>
          <p className="text-muted">
            En este momento no hay zonas sociales configuradas para reserva.
          </p>
        </div>
      </div>
    );
  }

  const zonasReservables = zonas.filter((zona) => zona.reservable);

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Zonas Sociales</h4>
        <LinkPrimary to="/mis-reservas" className="ms-auto">
          <i className="fas fa-calendar-alt me-2"></i>
          Mis Reservas
        </LinkPrimary>
      </div>

      <style>{`
        .zona-social-card:hover .zona-social-card-img {
          transform: scale(1.05);
        }
        .zona-social-card:hover {
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.2);
        }
      `}</style>

      <div className="row g-3">
        {zonasReservables.map((zona) => (
          <div key={zona.uuid} className="col-md-6 col-lg-4">
            <ZonaSocialCard zona={zona} onClick={handleCardClick} />
          </div>
        ))}
      </div>

      {zonasReservables.length === 0 && (
        <div className="col-12">
          <div className="alert alert-warning">
            No hay zonas sociales disponibles para reserva en este momento.
          </div>
        </div>
      )}

      <ReservaModal
        show={showModal}
        onHide={handleModalClose}
        zona={selectedZona}
        onSuccess={handleReservaSuccess}
      />
    </>
  );
};

export default ZonasDisponibles;
